/**
 * Chunk U4 — Persist incomplete upload drafts (Skip / Review later) for the
 * Invoices resume tray. Confirmed drafts are never stored here.
 *
 * Storage is venue-scoped localStorage (no base64 — resume uses library URLs).
 */

import {
  UPLOAD_DRAFT_STATUS,
  filterExpiredIncompleteUploads,
} from './invoiceUploadQueueHelpers'

export const INCOMPLETE_UPLOAD_KIND = {
  SKIPPED: 'skipped',
  REVIEW_LATER: 'review_later',
  FAILED: 'failed',
}

export function getIncompleteUploadsStorageKey(venueId) {
  return `invoices_incomplete_uploads_${String(venueId || '').trim()}`
}

function safeParse(raw) {
  try {
    const parsed = JSON.parse(raw)
    return Array.isArray(parsed) ? parsed : []
  } catch {
    return []
  }
}

export function loadIncompleteUploads(venueId) {
  if (!venueId || typeof localStorage === 'undefined') return []
  const raw = localStorage.getItem(getIncompleteUploadsStorageKey(venueId))
  if (!raw) return []
  return safeParse(raw)
    .filter(entry => entry && entry.invoiceId)
    .map(entry => ({
      ...entry,
      invoiceId: String(entry.invoiceId),
      draftId: String(entry.draftId || entry.invoiceId),
      needsReview: true,
    }))
}

export function saveIncompleteUploads(venueId, entries = []) {
  if (!venueId || typeof localStorage === 'undefined') return
  const list = (Array.isArray(entries) ? entries : [])
    .filter(e => e?.invoiceId)
    .map(e => ({
      ...e,
      invoiceId: String(e.invoiceId),
      draftId: String(e.draftId || e.invoiceId),
      needsReview: true,
    }))
  localStorage.setItem(
    getIncompleteUploadsStorageKey(venueId),
    JSON.stringify(list),
  )
}

export function countIncompleteUploads(venueId) {
  return loadIncompleteUploads(venueId).length
}

export function getIncompleteUploadInvoiceIds(venueId) {
  return new Set(loadIncompleteUploads(venueId).map(e => String(e.invoiceId)))
}

/**
 * Merge entries by invoiceId (latest wins). Drops confirmed/discarded statuses.
 */
export function upsertIncompleteUploads(venueId, incoming = []) {
  const existing = loadIncompleteUploads(venueId)
  const byId = new Map(existing.map(e => [String(e.invoiceId), e]))
  for (const entry of incoming || []) {
    if (!entry?.invoiceId) continue
    const status = String(entry.status || '')
    if (
      status === UPLOAD_DRAFT_STATUS.CONFIRMED ||
      status === UPLOAD_DRAFT_STATUS.DISCARDED
    ) {
      byId.delete(String(entry.invoiceId))
      continue
    }
    byId.set(String(entry.invoiceId), {
      ...byId.get(String(entry.invoiceId)),
      ...entry,
      invoiceId: String(entry.invoiceId),
      draftId: String(entry.draftId || entry.invoiceId),
      needsReview: true,
      updatedAt: entry.updatedAt || new Date().toISOString(),
    })
  }
  const next = [...byId.values()]
  saveIncompleteUploads(venueId, next)
  return next
}

export function removeIncompleteUploads(venueId, invoiceIds = []) {
  const remove = new Set((invoiceIds || []).map(id => String(id)))
  if (!remove.size) return loadIncompleteUploads(venueId)
  const next = loadIncompleteUploads(venueId).filter(
    e => !remove.has(String(e.invoiceId)),
  )
  saveIncompleteUploads(venueId, next)
  return next
}

export function clearIncompleteUploads(venueId) {
  if (!venueId || typeof localStorage === 'undefined') return
  localStorage.removeItem(getIncompleteUploadsStorageKey(venueId))
}

/**
 * Build a persistable incomplete entry from session draft + file.
 * Omits image base64 (too large for localStorage).
 */
export function buildIncompleteEntryFromSession({
  draft = {},
  file = {},
  incompleteKind = INCOMPLETE_UPLOAD_KIND.REVIEW_LATER,
} = {}) {
  const invoiceId = draft.invoiceId || file.invoiceId
  if (!invoiceId) return null
  const status = String(draft.status || UPLOAD_DRAFT_STATUS.READY)
  if (
    status === UPLOAD_DRAFT_STATUS.CONFIRMED ||
    status === UPLOAD_DRAFT_STATUS.DISCARDED
  ) {
    return null
  }

  let kind = incompleteKind
  if (status === UPLOAD_DRAFT_STATUS.SKIPPED) {
    kind = INCOMPLETE_UPLOAD_KIND.SKIPPED
  } else if (status === UPLOAD_DRAFT_STATUS.FAILED) {
    kind = INCOMPLETE_UPLOAD_KIND.FAILED
  }

  return {
    draftId: String(draft.id || file.draftId || invoiceId),
    invoiceId: String(invoiceId),
    pickIndex: Number.isFinite(Number(draft.pickIndex))
      ? Number(draft.pickIndex)
      : Number(file.pickIndex) || 0,
    filename: file.filename || draft.filename || 'Upload',
    status:
      status === UPLOAD_DRAFT_STATUS.READING ||
      status === UPLOAD_DRAFT_STATUS.QUEUED ||
      status === UPLOAD_DRAFT_STATUS.CONFIRMING
        ? UPLOAD_DRAFT_STATUS.READY
        : status,
    needsReview: true,
    incompleteKind: kind,
    documentType: file.documentType || draft.documentType || 'invoice',
    supplier: file.supplier || null,
    supplierTitle: file.supplierTitle || '',
    purchaseOrderNumber: file.purchaseOrderNumber || '',
    linkedOrderId: file.linkedOrderId || null,
    invoiceNumber: file.invoiceNumber || '',
    invoiceDate: file.invoiceDate || null,
    invoiceTotalInput: file.invoiceTotalInput || '',
    invoiceTotalSource: file.invoiceTotalSource || null,
    comment: file.comment || '',
    ocrStatus: file.ocrStatus || null,
    ocrSuggested: file.ocrSuggested || null,
    ocrWarnings: file.ocrWarnings || [],
    ocrError: file.ocrError || draft.error || null,
    createdAt: draft.createdAt || new Date().toISOString(),
    updatedAt: new Date().toISOString(),
  }
}

/**
 * Persist all unfinished session drafts (Review later / session close with skips).
 */
export function persistIncompleteSession({
  venueId,
  drafts = [],
  files = [],
  incompleteKind = INCOMPLETE_UPLOAD_KIND.REVIEW_LATER,
} = {}) {
  if (!venueId) return []
  const filesByDraftId = new Map(
    (files || [])
      .filter(f => f?.draftId != null)
      .map(f => [String(f.draftId), f]),
  )
  const entries = (drafts || [])
    .map(draft =>
      buildIncompleteEntryFromSession({
        draft,
        file: filesByDraftId.get(String(draft.id)) || {},
        incompleteKind,
      }),
    )
    .filter(Boolean)
  return upsertIncompleteUploads(venueId, entries)
}

/**
 * Simulate confirm-then-discard-remaining for tests / product honesty.
 * Returns { confirmedIds, discardedIds, incompleteRemaining }.
 */
export function applyConfirmThenDiscardRemainingSemantics({
  venueId,
  drafts = [],
  files = [],
  confirmDraftIds = [],
} = {}) {
  const confirmSet = new Set((confirmDraftIds || []).map(id => String(id)))
  const confirmedIds = []
  const discardFiles = []

  for (const draft of drafts || []) {
    const file = (files || []).find(f => String(f.draftId) === String(draft.id))
    if (confirmSet.has(String(draft.id))) {
      if (draft.invoiceId || file?.invoiceId) {
        confirmedIds.push(String(draft.invoiceId || file.invoiceId))
      }
      continue
    }
    if (
      draft.status !== UPLOAD_DRAFT_STATUS.CONFIRMED &&
      draft.status !== UPLOAD_DRAFT_STATUS.DISCARDED
    ) {
      discardFiles.push(file || { invoiceId: draft.invoiceId })
    }
  }

  removeIncompleteUploads(venueId, confirmedIds)
  const discardedIds = discardFiles
    .map(f => f?.invoiceId)
    .filter(Boolean)
    .map(String)
  removeIncompleteUploads(venueId, discardedIds)

  return {
    confirmedIds,
    discardedIds,
    incompleteRemaining: loadIncompleteUploads(venueId),
  }
}

/**
 * Rebuild drafts + lightweight files for resuming the review panel.
 * Preview uses library temp URL when available.
 */
export function rebuildUploadSessionFromIncomplete(
  entries = [],
  libraryInvoices = [],
) {
  const libraryById = new Map(
    (libraryInvoices || []).map(inv => [String(inv._id || inv.id || ''), inv]),
  )

  const drafts = []
  const files = []

  const sorted = [...(entries || [])].sort(
    (a, b) => Number(a.pickIndex || 0) - Number(b.pickIndex || 0),
  )

  sorted.forEach((entry, index) => {
    const invoiceId = String(entry.invoiceId)
    const library = libraryById.get(invoiceId) || {}
    const previewUrl =
      library.invoiceTempUrl ||
      library.invoiceUrl ||
      library.files?.[0]?.url ||
      ''
    const draftId = String(entry.draftId || invoiceId)
    const status =
      entry.status === UPLOAD_DRAFT_STATUS.SKIPPED
        ? UPLOAD_DRAFT_STATUS.SKIPPED
        : entry.status === UPLOAD_DRAFT_STATUS.FAILED
        ? UPLOAD_DRAFT_STATUS.FAILED
        : UPLOAD_DRAFT_STATUS.READY

    drafts.push({
      id: draftId,
      pickIndex: Number.isFinite(Number(entry.pickIndex))
        ? Number(entry.pickIndex)
        : index,
      filename: entry.filename || library.originalFilename || 'Upload',
      documentType: entry.documentType || library.documentType || 'invoice',
      status,
      error: entry.ocrError || null,
      invoiceId,
      createdAt: entry.createdAt || null,
      needsReview: true,
      incompleteKind:
        entry.incompleteKind || INCOMPLETE_UPLOAD_KIND.REVIEW_LATER,
    })

    files.push({
      draftId,
      pickIndex: Number.isFinite(Number(entry.pickIndex))
        ? Number(entry.pickIndex)
        : index,
      filename: entry.filename || library.originalFilename || 'Upload',
      image: { base64: previewUrl || '', isRemoteUrl: Boolean(previewUrl) },
      previewUrl,
      supplier: entry.supplier || library.supplier || null,
      supplierTitle: entry.supplierTitle || library.supplierTitle || '',
      purchaseOrderNumber:
        entry.purchaseOrderNumber || library.purchaseOrderNumber || '',
      linkedOrderId: entry.linkedOrderId || library.linkedOrderId || null,
      documentType: entry.documentType || library.documentType || 'invoice',
      comment: entry.comment || library.comment || '',
      invoiceNumber: entry.invoiceNumber || library.invoiceNumber || '',
      invoiceDate: entry.invoiceDate || library.invoiceDate || null,
      invoiceTotalInput: entry.invoiceTotalInput || '',
      invoiceTotalSource: entry.invoiceTotalSource || null,
      invoiceId,
      ocrStatus: entry.ocrStatus || 'idle',
      ocrSuggested: entry.ocrSuggested || null,
      ocrWarnings: entry.ocrWarnings || [],
      ocrError: entry.ocrError || null,
      needsReview: true,
    })
  })

  return { drafts, files }
}

/**
 * Drop expired incomplete entries (U5 helper; safe to call from tray load).
 */
export function pruneExpiredIncompleteUploads(venueId, options = {}) {
  const current = loadIncompleteUploads(venueId)
  const expired = filterExpiredIncompleteUploads(current, options)
  if (!expired.length) return { kept: current, removed: [] }
  const expiredIds = new Set(expired.map(e => String(e.invoiceId)))
  const kept = current.filter(e => !expiredIds.has(String(e.invoiceId)))
  saveIncompleteUploads(venueId, kept)
  return { kept, removed: expired }
}

/**
 * Matching-work rows excluding incomplete tray invoice ids
 * (Skip / Review later must not inflate the happy matching badge).
 */
export function filterRowsExcludingIncompleteUploads(
  rows = [],
  incompleteInvoiceIds = [],
) {
  const exclude =
    incompleteInvoiceIds instanceof Set
      ? incompleteInvoiceIds
      : new Set((incompleteInvoiceIds || []).map(id => String(id)))
  if (!exclude.size) return rows || []
  return (rows || []).filter(row => {
    const id = String(row?._id || row?.id || row?.invoiceId || '')
    return !id || !exclude.has(id)
  })
}
